import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Storefront, Eye, EyeSlash, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { t } = useI18n();
  const { loginWithToken } = useAuth();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Only merchants need to sign in. Customers browse the directory freely.
  useEffect(() => {
    if (params.get("as") === "client") {
      navigate("/annuaire", { replace: true });
    }
  }, [params, navigate]);
  const redirect = params.get("redirect") || "/dashboard";

  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);

  const emailLogin = async (e) => {
    e.preventDefault();
    if (!email || !pw) return;
    setBusy(true);
    try {
      const r = await api.post("/auth/login", { email, password: pw });
      loginWithToken(r.data.token, r.data.user);
      toast.success("Connexion réussie");
      navigate(redirect, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Email ou mot de passe incorrect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto max-w-md px-6 py-16 sm:py-24" data-testid="login-page">
      <Link to="/annuaire" className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="login-back-to-directory">
        ← {t.auth.browse_directory}
      </Link>
      <div className="label-uppercase mb-3 flex items-center gap-2">
        <Storefront size={12} weight="bold"/>
        {t.auth.space_pro}
      </div>
      <h1 className="font-display text-3xl sm:text-4xl tracking-tight font-medium mb-3">Connexion à votre espace</h1>
      <p className="text-muted-foreground mb-8">Entrez votre email et votre mot de passe.</p>

      <form onSubmit={emailLogin} className="space-y-3" data-testid="email-login-form">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t.auth.email_label} required autoComplete="email" className="w-full border border-border rounded-sm px-3 py-2.5 text-sm bg-background outline-none focus:border-primary" data-testid="login-email" />
        <div className="relative">
          <input type={showPw ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} placeholder={t.auth.password_label} required autoComplete="current-password" className="w-full border border-border rounded-sm px-3 py-2.5 pr-10 text-sm bg-background outline-none focus:border-primary" data-testid="login-password" />
          <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground transition-colors" aria-label={showPw ? "Masquer le mot de passe" : "Afficher le mot de passe"} data-testid="login-password-toggle">
            {showPw ? <EyeSlash size={18} weight="regular" /> : <Eye size={18} weight="regular" />}
          </button>
        </div>
        <button type="submit" disabled={busy || !email || !pw} className="w-full bg-foreground text-background hover:bg-foreground/90 px-4 py-3 rounded-sm font-semibold text-sm disabled:opacity-50" data-testid="login-submit">
          {busy ? "…" : t.auth.cta}
        </button>
        <div className="flex items-center justify-between text-xs">
          <Link to="/mot-de-passe-oublie" className="text-muted-foreground hover:text-foreground" data-testid="login-forgot-link">{t.auth.forgot}</Link>
          <Link to="/inscription" className="text-primary font-semibold" data-testid="login-register-link">{t.auth.create_account} →</Link>
        </div>
      </form>

      <div className="text-xs text-muted-foreground text-center leading-relaxed mt-6">
        {t.auth.terms_consent} <Link to="/cgu" className="underline text-foreground/80">{t.auth.terms}</Link> {t.auth.and} <Link to="/confidentialite" className="underline text-foreground/80">{t.auth.privacy}</Link>.
      </div>

      {/* Dedicated telepro entry — separate from merchant login on purpose:
          telepros authenticate via magic-link (no password) and the JWT/scope
          is different from a merchant account. */}
      <div className="mt-10 pt-6 border-t border-border text-center" data-testid="login-telepro-cta">
        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Vous êtes téléprospecteur·trice ?</div>
        <Link
          to="/teleprospecteur"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
          data-testid="login-telepro-link"
        >
          <Phone size={14} weight="bold"/> Accéder à mon espace téléprospection →
        </Link>
        <div className="text-[11px] text-muted-foreground mt-2">
          Connexion sans mot de passe (lien magique). Pas encore inscrit·e ? <Link to="/teleprospecteur/inscription" className="text-foreground/80 underline">Rejoindre l&apos;équipe</Link>.
        </div>
      </div>
    </div>
  );
}

export function AuthVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithToken } = useAuth();
  const { t } = useI18n();
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = params.get("token");
    const redirect = params.get("redirect") || "/dashboard";
    if (!token) { setError("Token manquant"); return; }
    api.post("/auth/verify", { token })
      .then((r) => {
        loginWithToken(r.data.token, r.data.user);
        navigate(redirect, { replace: true });
      })
      .catch((e) => setError(e?.response?.data?.detail || "Lien invalide"));
  }, [params, loginWithToken, navigate]);

  return (
    <div className="mx-auto max-w-md px-6 py-24 text-center" data-testid="auth-verify-page">
      {error ? (
        <div className="text-destructive">{error}</div>
      ) : (
        <div className="text-muted-foreground">{t.auth.verifying}</div>
      )}
    </div>
  );
}
